import { Mode } from "../../lib/ModeUtils";
import ICalculatorConfiguration from "../../models/ICalculatorConfiguration";
import IValidationResult from "../../models/IValidationResult";
import { PaperSizes } from "../../lib/PaperSizesUtils";
import { ICalculatorInput } from "../../models/ICalculatorInput";
import IPrintingOption from "../../models/IPrintingOption";

export default function validateCalculatorInput(
  calculatorInput: ICalculatorInput,
  pricingConfiguration: ICalculatorConfiguration,
): IValidationResult {
  const result = { isValid: true, errorMessages: [] } as IValidationResult;
  performChecks(calculatorInput, result, pricingConfiguration);
  const printingOption =
    pricingConfiguration.pricingConfiguration.printingOptions.filter(
      (x) => x.code === calculatorInput.selectedPrintingOption,
    )[0];

  if (
    calculatorInput.userType !== "school" &&
    calculatorInput.userType !== "individual"
  ) {
    result.errorMessages.push({
      propertyName: "userType",
      message: "User Type is required and must be individual or school.",
    });
  }

  if (calculatorInput.numCopies <= 0) {
    result.errorMessages.push({
      propertyName: "numCopies",
      message: "Number of Copies can't be 0 or negative.",
    });
  }

  if (Number.isNaN(calculatorInput.numCopies)) {
    result.errorMessages.push({
      propertyName: "numCopies",
      message: "Number of Copies can't be empty",
    });
  }
  if (
    calculatorInput.pagesBlack + calculatorInput.pagesColour === 1 &&
    calculatorInput.isDuplexSelected
  ) {
    result.errorMessages.push({
      propertyName: "isDuplexSelected",
      message: "Duplex Mode cannot be selected for a 1 page document.",
    });
  }
  if (
    calculatorInput.selectedMode === "mix" &&
    calculatorInput.isDuplexSelected &&
    !(printingOption.requireMultiplesOfFour || printingOption.requireDuplexMode)
  ) {
    result.errorMessages.push({
      propertyName: "isDuplexSelected",
      message: "Duplex Mode cannot be selected for a mixed mode document.",
    });
  }
  if (calculatorInput.pagesBlack + calculatorInput.pagesColour <= 0) {
    result.errorMessages.push({
      propertyName: "numPages",
      message: "Number of Pages can't be 0 or negative.",
    });
  }
  if (Number.isNaN(calculatorInput.pagesBlack + calculatorInput.pagesColour)) {
    result.errorMessages.push({
      propertyName: "numPages",
      message: "Number of Pages can't be empty",
    });
  }
  if (calculatorInput.selectedFinishingOption !== "") {
    const existingFinishingOptions =
      pricingConfiguration.pricingConfiguration.finishingOptions.filter(
        (fo) =>
          fo.guid === calculatorInput.selectedFinishingOption &&
          fo.availableToPrintingOptions.filter(
            (fo) => fo === calculatorInput.selectedPrintingOption,
          ).length > 0,
      );
    if (existingFinishingOptions.length === 0) {
      result.errorMessages.push({
        propertyName: "selectedFinishingOption",
        message:
          "A finishing option " +
          calculatorInput.selectedFinishingOption +
          " doesn't exist.",
      });
    }
  } else {
    result.errorMessages.push({
      propertyName: "selectedFinishingOption",
      message:
        "A finishing option " +
        calculatorInput.selectedFinishingOption +
        " is required.",
    });
  }

  preformPricingMatrixChecks(result, calculatorInput, pricingConfiguration);

  result.isValid = result.errorMessages.length === 0;

  return result;
}

function performChecks(
  calculatorInput: ICalculatorInput,
  result: IValidationResult,
  pricingConfiguration: ICalculatorConfiguration,
) {
  const existingPrintingOptions =
    pricingConfiguration.pricingConfiguration.printingOptions.filter(
      (po) => po.code === calculatorInput.selectedPrintingOption,
    );
  if (existingPrintingOptions.length === 0) {
    result.errorMessages.push({
      propertyName: "selectedPrintingOption",
      message:
        "A printing option with code: " +
        calculatorInput.selectedPrintingOption +
        " doesn't exist.",
    });
  }
  if (existingPrintingOptions.length > 0) {
    printingOptionValidation(
      calculatorInput,
      existingPrintingOptions[0],
      result,
      pricingConfiguration,
    );
  }

  const existingPaperTypes =
    pricingConfiguration.pricingConfiguration.paperTypes.filter(
      (pt) => pt.guid === calculatorInput.selectedPaperType,
    );
  if (existingPaperTypes.length === 0) {
    result.errorMessages.push({
      propertyName: "selectedPaperType",
      message:
        "A paper type with guid: " +
        calculatorInput.selectedPaperType +
        " doesn't exist.",
    });
  }

  const existingPaperSize = PaperSizes.filter(
    (ps) => ps.code === calculatorInput.selectedPaperSize,
  );
  if (existingPaperSize.length === 0) {
    result.errorMessages.push({
      propertyName: "selectedPaperSize",
      message:
        "A paper size with code: " +
        calculatorInput.selectedPaperSize +
        " doesn't exist.",
    });
  }

  const existingMode = Mode.filter(
    (mode) => mode.code === calculatorInput.selectedMode,
  );
  if (existingMode.length === 0) {
    result.errorMessages.push({
      propertyName: "selectedMode",
      message:
        "Mode with code: " + calculatorInput.selectedMode + " doesn't exist.",
    });
  }
}

function printingOptionValidation(
  calculatorInput: ICalculatorInput,
  printingOption: IPrintingOption,
  result: IValidationResult,
  pricingConfiguration: ICalculatorConfiguration,
) {
  if (printingOption.requireDuplexMode && !calculatorInput.isDuplexSelected) {
    result.errorMessages.push({
      propertyName: "isDuplexSelected",
      message: printingOption.name + " requires Is Duplex to be selected.",
    });
  }
  if (
    printingOption.maximumPagesPerCopy !== -1 &&
    calculatorInput.pagesBlack + calculatorInput.pagesColour >
      printingOption.maximumPagesPerCopy
  ) {
    result.errorMessages.push({
      propertyName: "numPages",
      message:
        printingOption.name +
        " cannot have more than " +
        printingOption.maximumPagesPerCopy +
        " pages per copy.",
    });
  }
  if (
    printingOption.requireMultiplesOfFour &&
    (calculatorInput.pagesBlack + calculatorInput.pagesColour) % 4 !== 0
  ) {
    result.errorMessages.push({
      propertyName: "numPages",
      message:
        printingOption.name +
        " requires total number of pages (excluding cover page) to be multiples of 4. Refer to instructions above on how to construct the file correctly.",
    });
  }
  if (
    !printingOption.supportsMixMode &&
    calculatorInput.selectedMode === "mix"
  ) {
    result.errorMessages.push({
      propertyName: "selectedMode",
      message: printingOption.name + " does not support mixed colour mode.",
    });
  }

  const totalPages =
    (calculatorInput.pagesBlack + calculatorInput.pagesColour) *
    calculatorInput.numCopies;

  if (totalPages < printingOption.minimumPagesPerJob) {
    result.errorMessages.push({
      propertyName: "numPages",
      message:
        printingOption.name +
        " requires total number of pages (including all copies) to be at least " +
        printingOption.minimumPagesPerJob +
        ".",
    });
  }

  validateCover(printingOption, calculatorInput, result, pricingConfiguration);
}
function validateCover(
  printingOption: IPrintingOption,
  calculatorInput: ICalculatorInput,
  result: IValidationResult,
  pricingConfiguration: ICalculatorConfiguration,
) {
  if (printingOption.hasCover) {
    if (
      calculatorInput.selectedCoverPaperType == null ||
      (calculatorInput.selectedCoverPaperType.length ?? 0) === 0
    ) {
      result.errorMessages.push({
        propertyName: "selectedCoverPaperType",
        message: "Cover Paper Type is required.",
      });
    }

    if (
      calculatorInput.selectedCoverMode === null ||
      (calculatorInput.selectedCoverMode?.length ?? 0) === 0
    ) {
      result.errorMessages.push({
        propertyName: "selectedCoverMode",
        message: "Cover Printing Mode is required.",
      });
    }

    const insidePaperType =
      pricingConfiguration.pricingConfiguration.paperTypes.filter(
        (pt) => pt.guid === calculatorInput.selectedPaperType,
      );
    if (insidePaperType.length > 0) {
      if (!insidePaperType[0].useForInsideInBooklet) {
        result.errorMessages.push({
          propertyName: "selectedPaperType",
          message: "Inside Paper Type is invalid.",
        });
      }
    }
  } else {
    if ((calculatorInput.selectedCoverPaperType?.length ?? 0) > 0) {
      result.errorMessages.push({
        propertyName: "selectedCoverPaperType",
        message:
          "Cover Paper Type is not supported in " + printingOption.name + ".",
      });
    }
  }
}
function preformPricingMatrixChecks(
  result: IValidationResult,
  calculatorInput: ICalculatorInput,
  pricingConfiguration: ICalculatorConfiguration,
) {
  const matchingPricingMatrices =
    pricingConfiguration.pricingConfiguration.pricingMatrix.filter(
      (pm) =>
        pm.selectedPrintingOption === calculatorInput.selectedPrintingOption &&
        pm.paperTypes.filter((pt) => pt === calculatorInput.selectedPaperType)
          .length > 0 &&
        pm.selectedPaperSize === calculatorInput.selectedPaperSize &&
        pm.duplex === calculatorInput.isDuplexSelected,
    );
  if (matchingPricingMatrices.length === 0) {
    result.errorMessages.push({
      propertyName: "pricingMatrix",
      message: "No pricing matrix found for the selected options.",
    });
  } else {
    const numberOfPages =
      calculatorInput.pagesBlack +
      calculatorInput.pagesColour * calculatorInput.numCopies;
    const matchingPricingMatrixWithPages = matchingPricingMatrices.filter(
      (pm) =>
        pm.pageRangeLow <= numberOfPages && pm.pageRangeHigh >= numberOfPages,
    );
    if (matchingPricingMatrixWithPages.length === 0) {
      result.errorMessages.push({
        propertyName: "numPages",
        message:
          "No pricing matrix found for the selected options and page quantity.",
      });
    }
  }
}
